import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import AppVersionService from "src/Services/API/appversion.service";
import { isSuperAdmin } from "src/Common/function";

// Numeric-segment compare ("2.3.10" > "2.3.8"). Enough for the app's versioning;
// a plain string compare would get that pair backwards.
const compareVersions = (a, b) => {
  const left = String(a).split(".");
  const right = String(b).split(".");
  const length = Math.max(left.length, right.length);
  for (let i = 0; i < length; i++) {
    const diff = (parseInt(left[i], 10) || 0) - (parseInt(right[i], 10) || 0);
    if (diff !== 0) return diff < 0 ? -1 : 1;
  }
  return 0;
};

const STATUS = {
  UP_TO_DATE: "Up to date",
  OUTDATED: "Outdated",
  AHEAD: "Ahead",
  NEVER: "Never opened",
};

const STATUS_STYLE = {
  [STATUS.UP_TO_DATE]: "text-green-600",
  [STATUS.OUTDATED]: "text-red-500",
  [STATUS.AHEAD]: "text-orange-500",
  [STATUS.NEVER]: "text-subTitleColor",
};

const FILTERS = ["All", STATUS.OUTDATED, STATUS.UP_TO_DATE, STATUS.NEVER];

function AppVersions() {
  const navigate = useNavigate();
  const [latest, setLatest] = useState({});
  const [devices, setDevices] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [filter, setFilter] = useState("All");

  // Defend the page against direct URL access — the menu item is already gated,
  // but the route should not render for anyone but a Super Admin.
  useEffect(() => {
    let allowed = false;
    try {
      allowed = isSuperAdmin();
    } catch (e) {
      allowed = false;
    }
    if (!allowed) {
      navigate("/");
      return;
    }
    load();
  }, []);

  const load = async () => {
    try {
      const response = await AppVersionService.list();
      if (response.status === 200) {
        setLatest(response.data?.latest || {});
        setDevices(response.data?.rows || []);
      } else {
        toast("Something went wrong!");
      }
    } catch (error) {
      toast(error?.response?.data?.message || "Failed to load app versions");
    }
  };

  // One row per device, so a user with an iPhone and an Android tablet appears
  // twice — each install can be behind independently. Status is measured against
  // the published build for THAT platform: iOS and Android can sit on different
  // versions during a staged release.
  const rows = useMemo(() => {
    const withStatus = devices.map((device) => {
      const published = latest[device.platform];
      let status = STATUS.NEVER;
      if (device.appVersion && published) {
        const diff = compareVersions(device.appVersion, published);
        status = diff < 0 ? STATUS.OUTDATED : diff > 0 ? STATUS.AHEAD : STATUS.UP_TO_DATE;
      } else if (device.appVersion) {
        status = STATUS.UP_TO_DATE;
      }
      return { ...device, status };
    });

    // Users who have actually reported a platform and version come first (most
    // recently seen on top); everyone who has never opened the app trails,
    // alphabetically. Sorted here rather than trusting the API's row order.
    return withStatus.sort((a, b) => {
      const aReported = a.appVersion ? 1 : 0;
      const bReported = b.appVersion ? 1 : 0;
      if (aReported !== bReported) return bReported - aReported;
      if (aReported) {
        return new Date(b.lastSeenAt || 0) - new Date(a.lastSeenAt || 0);
      }
      return String(a.name || "").localeCompare(String(b.name || ""));
    });
  }, [devices, latest]);

  const visibleRows = useMemo(() => {
    const search = keyword.trim().toLowerCase();
    return rows.filter((row) => {
      if (filter !== "All" && row.status !== filter) return false;
      if (!search) return true;
      return [row.name, row.email, row.companyName, row.companyIdentifier, row.appVersion]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(search));
    });
  }, [rows, keyword, filter]);

  // Counted over devices that have actually reported — a user who has never
  // opened the app isn't "behind on the rollout", they're absent from it, and
  // mixing the two would understate adoption.
  const adoption = useMemo(() => {
    const reported = rows.filter((row) => row.status !== STATUS.NEVER);
    const current = reported.filter((row) => row.status === STATUS.UP_TO_DATE);
    return {
      reported: reported.length,
      current: current.length,
      outdated: reported.length - current.length,
      percent: reported.length ? Math.round((current.length / reported.length) * 100) : 0,
    };
  }, [rows]);

  return (
    <div className="w-full h-full bg-backgroundColor p-5 overflow-auto">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Stat
          label="On latest"
          value={`${adoption.current} / ${adoption.reported}`}
          hint={`${adoption.percent}% of devices`}
        />
        <Stat label="Outdated" value={adoption.outdated} hint="Behind the published build" />
        <Stat
          label="Published"
          value={`iOS ${latest.ios || "—"} · Android ${latest.android || "—"}`}
          hint="Force-update target"
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          {FILTERS.map((option) => (
            <span
              key={option}
              onClick={() => setFilter(option)}
              className={`px-3 py-1 rounded-[6px] cursor-pointer text-[12px] ${
                filter === option
                  ? "bg-blueColor text-white font-OpenSansSemiBold"
                  : "bg-white border border-borderColor text-subTitleColor font-OpenSansMedium"
              }`}
            >
              {option}
            </span>
          ))}
        </div>
        <input
          value={keyword}
          onChange={(event) => setKeyword(event.target.value)}
          placeholder="Search name, email, tenant or version"
          className="h-[30px] w-[300px] px-3 rounded-[6px] border border-borderColor bg-white text-[12px] font-OpenSansRegular text-titleColor outline-none"
        />
      </div>

      <div className="w-full bg-white rounded-[6px] overflow-hidden">
        <div className="grid grid-cols-[0.4fr_1.8fr_1.2fr_1fr_0.8fr_0.8fr_1fr_1.1fr] gap-2 px-4 py-3 border-b border-borderColor">
          {["#", "Name", "Tenant", "Role", "Platform", "Version", "Status", "Last seen"].map((header) => (
            <span key={header} className="text-[12px] font-OpenSansSemiBold text-subTitleColor">
              {header}
            </span>
          ))}
        </div>

        {visibleRows.length === 0 && (
          <div className="px-4 py-6 text-[12px] font-OpenSansMedium text-subTitleColor">
            {rows.length === 0 ? "No users yet." : "No users match this filter."}
          </div>
        )}

        {visibleRows.map((row, index) => (
          <div
            key={row.deviceId ? `d${row.deviceId}` : `u${row.userId}`}
            className="grid grid-cols-[0.4fr_1.8fr_1.2fr_1fr_0.8fr_0.8fr_1fr_1.1fr] gap-2 px-4 py-3 border-b border-borderColor items-center"
          >
            <span className="text-[13px] font-OpenSansRegular text-subTitleColor">{index + 1}</span>
            <div className="min-w-0">
              <p className="text-[13px] font-OpenSansMedium text-titleColor truncate">{row.name || "—"}</p>
              <p className="text-[11px] font-OpenSansRegular text-subTitleColor truncate">{row.email}</p>
            </div>
            <span className="text-[13px] font-OpenSansRegular text-subTitleColor truncate">
              {row.companyName || row.companyIdentifier || "—"}
            </span>
            <span className="text-[13px] font-OpenSansRegular text-subTitleColor truncate">{row.role || "—"}</span>
            <span className="text-[13px] font-OpenSansRegular text-subTitleColor capitalize">
              {row.platform === "ios" ? "iOS" : row.platform || "—"}
            </span>
            <span className="text-[13px] font-OpenSansMedium text-titleColor">{row.appVersion || "—"}</span>
            <span className={`text-[12px] font-OpenSansSemiBold ${STATUS_STYLE[row.status]}`}>{row.status}</span>
            <span className="text-[12px] font-OpenSansRegular text-subTitleColor">
              {row.lastSeenAt ? moment(row.lastSeenAt).fromNow() : "—"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value, hint }) {
  return (
    <div className="px-4 py-3 bg-white rounded-[6px] border border-borderColor min-w-[180px]">
      <p className="text-[11px] font-OpenSansSemiBold text-subTitleColor uppercase">{label}</p>
      <p className="text-[16px] font-OpenSansBold text-titleColor mt-1">{value}</p>
      <p className="text-[11px] font-OpenSansRegular text-subTitleColor">{hint}</p>
    </div>
  );
}

export default AppVersions;
